import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Divider,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Snackbar,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  Container,
  ContactInfoCard,
  ContactInfoItem,
  PageHeader,
  FAQSection,
  StyledAccordion,
  ContactDetailsSection
} from './styled';

const faqData = [
  {
    question: "How does HiveSpace work?",
    answer: "HiveSpace helps optimize coworking spaces by making them available on typically quieter days, like Mondays and Fridays, while fostering communities built around shared interests. Hosts list their spaces with details about amenities and availability, while users can easily search for and book workspaces that fit their needs."
  },
  {
    question: "What types of spaces are available on HiveSpace?",
    answer: "We offer a variety of workspace options including hot desks, dedicated desks, private offices, meeting rooms, event spaces, and more. Our venues range from traditional coworking spaces to cafés, hotels, and other unique environments that provide suitable workspace settings."
  },
  {
    question: "How do I book a space?",
    answer: "Booking is simple! Search for spaces based on your location and requirements, browse through the options, and select the one that suits your needs. You can view availability in real-time and make instant bookings for many spaces. For some venues, booking requests may need to be approved by the host."
  },
  {
    question: "Can I cancel or modify my booking?",
    answer: "Yes, you can cancel or modify your booking according to the cancellation policy of the specific venue. Most venues allow free cancellation up to 24-48 hours before your booking. Check the venue's listing for specific policies."
  },
  {
    question: "How do payments work?",
    answer: "We handle all payments securely through our platform. You'll be charged according to the rates listed for your chosen workspace. Some venues offer hourly, daily, or monthly rates. All prices include applicable taxes but we may apply a service fees, which are clearly displayed before you confirm your booking."
  },
  {
    question: "I'm a space owner. How can I list my space on HiveSpace?",
    answer: "We'd love to have your space on our platform! You can get started by contacting us. Our team will guide you through the process of creating your listing and optimizing it to attract the right users."
  },
  {
    question: "What if I have issues during my booking?",
    answer: "Our support team is available to help with any issues that arise during your booking. You can contact us through the support section in your account, or reach out via email at hello@hivespace.co. For urgent matters, we also provide phone support during business hours."
  }
];

const Contact = () => {
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);


  const handleSnackbarClose = () => {
    setSubmitSuccess(false);
    setSubmitError(false);
  };

  return (
    <Container>
      <PageHeader>
        <Typography variant="h3" component="h1">
          Help Center
        </Typography>
        <Typography variant="subtitle1">
          Have questions or need help? We&apos;re here for you.
        </Typography>
      </PageHeader>

      <Grid container spacing={4}>
        <Grid item xs={12} md={5}>
          <ContactDetailsSection>

            <ContactInfoCard elevation={0}>
              <ContactInfoItem>
                <EmailIcon color="primary" />
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    Email Us
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <a href="mailto:hello@hivespace.co">hello@hivespace.co</a>
                  </Typography>
                </Box>
              </ContactInfoItem>

              <Divider sx={{ my: 2 }} />

              <ContactInfoItem>
                <PhoneIcon color="primary" />
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    Call Us
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <a href="tel:+4407565785254">+44 (0) 7565 785 254</a>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Monday to Friday, 9am - 5pm GMT
                  </Typography>
                </Box>
              </ContactInfoItem>

              <Divider sx={{ my: 2 }} />

              <ContactInfoItem>
                <LocationOnIcon color="primary" />
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    Visit Us
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hive B Ltd<br />
                    6 - 8 Bonhill Street<br />
                    London, EC2A 4BX<br />
                    United Kingdom
                  </Typography>
                </Box>
              </ContactInfoItem>
            </ContactInfoCard>
          </ContactDetailsSection>
        </Grid>

        <Grid item xs={12} md={7}>
          <FAQSection>
            <Typography variant="h4" component="h2" sx={{ mb: 4, fontWeight: 600, textAlign: 'center' }}>
              Frequently Asked Questions
            </Typography>

            <Box>
              {faqData.map((faq, index) => (
                <StyledAccordion key={index} disableGutters elevation={0}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`faq-content-${index}`}
                    id={`faq-header-${index}`}
                  >
                    <Typography variant="subtitle1" fontWeight={500}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" color="text.secondary">
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </StyledAccordion>
              ))}
            </Box>
          </FAQSection>
        </Grid>
      </Grid>

      {/* FAQ Section */}


      {/* Success Notification */}
      <Snackbar
        open={submitSuccess}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" variant="filled">
          Your message has been sent successfully! We&apos;ll get back to you soon.
        </Alert>
      </Snackbar>

      {/* Error Notification */}
      <Snackbar
        open={submitError}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error" variant="filled">
          There was an error sending your message. Please try again.
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Contact;